import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { tasksAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { Plus, ListChecks } from "lucide-react";
import { format } from "date-fns";

/**
 * Reusable Tasks section for source module detail pages (Spec Section 6).
 * Shows task list for a given source record + "Create task" button (DPO only).
 *
 * Props:
 *  - showProgress: boolean — renders "X of Y complete" header + progress bar
 *    (used on DPIA for "Remediation status" + Assessments for "findings vs completed")
 *  - progressLabel: string — override the default "X of Y complete" wording
 */
export function TasksSection({
  sourceModule,
  sourceId,
  clientId,
  entityId,
  title = "Tasks",
  showProgress = false,
  progressLabel,
}) {
  const navigate = useNavigate();
  const { isDPO, isComplianceReadOnly } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const cannotWrite = isComplianceReadOnly;

  useEffect(() => {
    if (!sourceModule || !sourceId) return;
    loadTasks();
  }, [sourceModule, sourceId]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const res = await tasksAPI.getBySource(sourceModule, sourceId);
      setTasks(res.data || []);
    } catch (err) {
      console.error("Failed to load tasks:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    const params = new URLSearchParams({
      source_module: sourceModule,
      source_id: sourceId,
    });
    if (clientId) params.set("client_id", clientId);
    if (entityId) params.set("entity_id", entityId);
    navigate(`/operations/tasks?${params.toString()}`);
  };

  const statusColors = {
    pending: "secondary",
    in_progress: "default",
    blocked: "destructive",
    completed: "success",
  };

  const completedCount = tasks.filter((t) => t.status === "completed").length;
  const percent = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
  const defaultProgressLabel = `${completedCount} of ${tasks.length} complete`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <ListChecks className="h-4 w-4" />
          {title}
          {tasks.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {tasks.length}
            </Badge>
          )}
        </CardTitle>
        {!cannotWrite && (
          <Button size="sm" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-1" />
            Create task
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading tasks…</p>
        ) : tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tasks linked to this record yet.</p>
        ) : (
          <>
            {showProgress && (
              <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">
                    {progressLabel || defaultProgressLabel}
                  </p>
                  <span className="text-sm text-muted-foreground">{percent}%</span>
                </div>
                <Progress value={percent} className="h-2" />
              </div>
            )}
            <div className="space-y-2">
              {tasks.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between p-3 rounded border border-border hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/operations/tasks?task=${t.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    {t.due_date && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Due {format(new Date(t.due_date), "MMM d, yyyy")}
                      </p>
                    )}
                  </div>
                  <Badge variant={statusColors[t.status] || "secondary"} className="ml-3 capitalize">
                    {t.status?.replace("_", " ")}
                  </Badge>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default TasksSection;
